'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';

import { HeaderCartButton } from '@eliadmin/core/src/components/header/header-cart-button';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import { LanguageSelect } from '@eliadmin/core/src/components/header/language-select';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';

import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryMenuItem } from './category-menu';
import { fetchMe } from '@eliadmin/core/src/api/customers/customers-api';
import { useEffect, useState } from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { LoggedOffDialog } from '../global-websockets/logged-of-dialog';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  templateSettings: TemplateSettings['data'];
  menu: Menu[];
  menu2: Menu[];
};

export function HeaderMain({ generalLayout, templateSettings, menu, menu2 }: HeaderMainProps) {
  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);
  const [loggedOff, setLoggedOff] = useState(false);
  const { t } = useTranslations();
  const subscriptionHref = me ? ROUTES.SUBSCRIPTIONS() : ROUTES.SUBSCRIPTION();

  useEffect(() => {
    if (!me?.authToken) return;
    const id = setInterval(() => {
      fetchMe({ locale, authToken: me.authToken }).catch(() => {
        setLoggedOff(true);
      });
    }, 60_000);
    return () => clearInterval(id);
  }, [locale, me?.authToken]);

  return (
    <MobileSearchProvider>
      {loggedOff && (
        <ResponsiveDialogProvider>{loggedOff && <LoggedOffDialog />}</ResponsiveDialogProvider>
      )}
      <div className={cn('bg-black', 'ea-header-main', 'custom-section', 'pr-2 lg:pr-6')}>
        <div className={cn('custom-container', 'hidden items-center justify-between py-6 lg:flex')}>
          <Link prefetch={true} href={ROUTES.HOME()} aria-label={t('text160')}>
            <Image
              src={generalLayout.header?.logo.url}
              alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company)}
              width={0}
              height={0}
              className="max-h-10 min-h-10 w-auto object-contain xl:max-h-12 xl:min-h-12"
              sizes="300px"
            />
          </Link>
          <div className="flex items-center justify-end gap-4 lg:gap-6 xl:gap-8">
            <ul data-ea-visual-tag="div" className="flex items-center justify-center gap-4 lg:gap-6 xl:gap-8">
              {menu[0] &&
                menu[0].fields.map(item => (
                  <CategoryMenuItem key={item.path} href={item.path} title={item.name} />
                ))}
              <CategoryMenuItem
                key="subscription"
                href={subscriptionHref}
                title={t('text1917')}
                isSubscription
              />
            </ul>
            <div className="flex items-center justify-center gap-2">
              <HeaderProfileButton />
              <HeaderCartButton />
            </div>
            {templateSettings.header?.language?.enabled && <LanguageSelect white={true} />}
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-3.5 lg:hidden">
          <div className="flex w-[64px] items-center justify-start gap-2">
            <MobileSidebar menu={menu2[0] && menu2[0].fields} side="left" />
          </div>
          <Link prefetch={true} href={ROUTES.HOME()} aria-label={t('text160')}>
            <Image
              src={generalLayout.header?.logo.url}
              alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company)}
              width={0}
              height={0}
              className="max-h-7 min-h-7 w-auto object-contain sm:max-h-10 sm:min-h-10"
            />
          </Link>
          <div className="flex items-center justify-center gap-1">
            <HeaderProfileButton />
            <HeaderCartButton />
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
